import { datadogRum } from '@datadog/browser-rum'
import * as types from '@script/types'
import * as Sentry from '@sentry/react'

import { clientSessionEndpoints } from './client-session'
import { commonEndpoints } from './common'
import { eventEndpoints } from './events'
import { directTaskEndpoints } from './tasks'
import { Api, FetchClient, FetchOptions, FetchResponse } from './types'

const FILE_UPLOAD_ENDPOINTS = ['/files', '/upload_and_classify']

const baseUrls = {
  [types.Env.Production]: 'https://app.truework.com',
  [types.Env.Sandbox]: 'https://app.truework-sandbox.com',
}

function getDefaultHeaders(url: string, sessionToken: string, version: string): HeadersInit {
  const headers: HeadersInit = {
    Accept: 'application/json; version=2023-10-30',
    Authorization: `Token ${sessionToken}`,
    'Content-Type': 'application/json',
  }

  if (version) {
    headers['X-TW-Version'] = version
  }

  if (FILE_UPLOAD_ENDPOINTS.some((endpoint) => url.includes(endpoint))) {
    delete headers['Content-Type']
  }

  return headers
}

async function handleResponse<T, E>(response: Response): Promise<FetchResponse<T, E>> {
  const data = await response.json().catch(() => null)
  const hasData = Boolean(data) && response.ok

  addSentryBreadcrumb(response.url, 'fetch.response', 'debug', {
    response: {
      status: response.status,
      hasData,
      hasError: !response.ok,
    },
  })

  return { data: hasData ? data : undefined, response, error: !response.ok ? data : undefined }
}

function addSentryBreadcrumb(message: string, category: string, level: Sentry.SeverityLevel, data: object = {}) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  })
}

export function createFetchClient({
  baseUrl,
  sessionToken,
  version,
}: {
  baseUrl: string
  sessionToken: string
  version: string
}): FetchClient {
  return async <T, E>(url: string, options: FetchOptions = {}): Promise<FetchResponse<T, E>> => {
    const fullUrl = `${baseUrl}${url}`
    const defaultHeaders = getDefaultHeaders(fullUrl, sessionToken, version)

    const fetchOptions: FetchOptions = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      timeout: options.timeout || 60 * 1000,
    }

    addSentryBreadcrumb(`${options.method || 'GET'} ${fullUrl}`, 'fetch.request', 'debug')

    try {
      const response = await fetch(fullUrl, fetchOptions)

      return await handleResponse<T, E>(response)
    } catch (error) {
      Sentry.captureException(error)
      datadogRum.addError(error as Error)
      throw error
    }
  }
}

export function create({
  sessionToken,
  version,
  env = types.Env.Production,
}: {
  sessionToken: string
  version: string
  env?: types.Env
}): Api {
  const fetchClient = createFetchClient({ baseUrl: process.env.TJS_API_ORIGIN || baseUrls[env], sessionToken, version })

  return {
    clientSession: clientSessionEndpoints(fetchClient),
    tasks: directTaskEndpoints(fetchClient),
    events: eventEndpoints(fetchClient),
    common: commonEndpoints(fetchClient),
  }
}
